import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Key, Users, TrendingUp, Gamepad2, Coins, Calendar, ArrowRight, Sparkles } from "lucide-react";
import { useState, useEffect, useRef } from "react";

const creatorSteps = [
  {
    icon: Users,
    title: "Discover Indie Games",
    description: "Browse our curated collection of indie games across all genres. Find titles that match your content style, audience preferences, and streaming schedule. Filter by genre, release date, and developer to find the perfect games for your channel."
  },
  {
    icon: Key,
    title: "Request Free Game Keys",
    description: "Send key requests directly to indie game developers with your creator profile and channel statistics. Developers can review your content history and approve requests based on your audience reach and engagement metrics."
  },
  {
    icon: TrendingUp,
    title: "Create & Share Content",
    description: "Produce engaging gameplay videos, reviews, and streams featuring indie games. Help independent developers reach new audiences while growing your channel with unique content that stands out from mainstream titles."
  }
];

const studioSteps = [
  {
    icon: Key,
    title: "Upload & Distribute Game Keys",
    description: "Add your indie game to IndieKeys with Steam App ID or Itch.io integration. Upload promotional keys in bulk, set distribution limits, and manage your key inventory with our secure key management system. Create professional press kits with screenshots, trailers, and game information."
  },
  {
    icon: Users,
    title: "Connect with Content Creators",
    description: "Browse verified content creators filtered by platform (YouTube, Twitch, TikTok), audience size, and game genre preferences. Review creator profiles with channel statistics, past coverage, and engagement metrics to find the perfect match for your game marketing strategy."
  },
  {
    icon: TrendingUp,
    title: "Track Coverage & Analytics",
    description: "Monitor key distribution in real-time with comprehensive analytics dashboard. Track which creators received keys, view content coverage, measure audience reach, and analyze the impact of your influencer marketing campaigns with detailed performance metrics."
  }
];

const playerSteps = [
  {
    icon: Gamepad2,
    title: "Discover Hidden Indie Gems",
    description: "Explore our curated library of indie games across all genres including action, adventure, puzzle, simulation, and RPG. Find hidden gems before they become mainstream, read community reviews, and add games to your wishlist for future releases."
  },
  {
    icon: Sparkles,
    title: "Earn Gems Through Missions",
    description: "Complete daily and weekly missions to earn platform gems. Activities include logging in daily, liking games, adding favorites, visiting game pages, and engaging with the community. Use earned gems to purchase indie games directly on the platform."
  },
  {
    icon: Calendar,
    title: "Track Game Releases",
    description: "Stay updated with upcoming indie game releases and early access launches. Get notifications for games on your wishlist, follow your favorite developers, and never miss a release date. Access exclusive early bird offers and limited-time promotions."
  }
];

export const HowItWorks = () => {
  const [activeTab, setActiveTab] = useState("studios");
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const renderSteps = (steps: any[], colorClass: string, iconClass: string) => (
    <div className="space-y-8">
      {steps.map((step, index) => (
        <div
          key={step.title}
          className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          style={{ transitionDelay: `${index * 200}ms` }}
        >
          {/* Image/Visual Block */}
          <div className="flex-1 max-w-sm">
            <div className="relative group">
              <div className={`aspect-square rounded-xl bg-gradient-to-br ${colorClass}/20 to-${colorClass}/5 p-6 flex items-center justify-center relative overflow-hidden`}>
                {/* Decorative elements */}
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
                <div className={`absolute top-3 right-3 w-12 h-12 bg-${colorClass}/10 rounded-full blur-xl`}></div>
                <div className={`absolute bottom-3 left-3 w-8 h-8 bg-${colorClass}/20 rounded-full blur-lg`}></div>

                {/* Main icon */}
                <div className="relative z-10">
                  <div className={`w-16 h-16 mx-auto mb-3 rounded-full bg-${colorClass}/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                    <step.icon className={`w-8 h-8 text-${colorClass}`} />
                  </div>
                  <div className={`text-6xl font-bold text-${colorClass}/20 text-center group-hover:text-${colorClass}/30 transition-colors duration-500`}>
                    {(index + 1).toString().padStart(2, '0')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Text Block */}
          <div className="flex-1 max-w-lg">
            <div className="space-y-3">
              <h4 className={`text-2xl font-bold text-${colorClass}`}>{step.title}</h4>
              <p className="text-base text-muted-foreground leading-relaxed">{step.description}</p>
              <div className={`flex items-center gap-2 text-${colorClass}/60`}>
                <div className={`w-6 h-0.5 bg-${colorClass}/30`}></div>
                <span className="text-sm font-medium">Step {index + 1}</span>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section ref={sectionRef} className="py-20 px-4 bg-gradient-to-br from-card/30 via-background to-card/20 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="absolute top-10 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl"></div>
      <div className="absolute bottom-10 right-10 w-32 h-32 bg-secondary/10 rounded-full blur-xl"></div>

      <div className="container relative">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 mb-4">
            <Sparkles className="w-6 h-6 text-primary" />
            <span className="text-sm font-medium text-primary">Simple Process</span>
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <h2 className="text-[2.5rem] md:text-[4rem] font-bold text-center mb-6">
            How It <span className="gradient-text">Works</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Three different paths, one amazing platform. Choose your journey and start connecting with the indie gaming community.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 mb-12 bg-card/50 backdrop-blur-sm h-auto">
            <TabsTrigger value="creators" className="flex items-center gap-2 justify-center py-3 md:py-2">
              <Users className="w-4 h-4" />
              <span className="hidden sm:inline">For Content Creators</span>
              <span className="sm:hidden">Creators</span>
            </TabsTrigger>
            <TabsTrigger value="studios" className="flex items-center gap-2 justify-center py-3 md:py-2">
              <Key className="w-4 h-4" />
              <span className="hidden sm:inline">For Studios</span>
              <span className="sm:hidden">Studios</span>
            </TabsTrigger>
            <TabsTrigger value="gamers" className="flex items-center gap-2 justify-center py-3 md:py-2">
              <Gamepad2 className="w-4 h-4" />
              <span className="hidden sm:inline">For Gamers</span>
              <span className="sm:hidden">Gamers</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="creators" className="mt-0">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-8 h-8 px-2 rounded-full bg-accent/20 flex items-center justify-center">
                  <Users className="w-4 h-4 text-accent" />
                </div>
                <h3 className="text-4xl font-bold">For Content Creators</h3>
              </div>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Discover amazing indie games and create engaging content for your audience
              </p>
            </div>
            {renderSteps(creatorSteps, "accent", "accent")}
          </TabsContent>

          <TabsContent value="studios" className="mt-0">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-8 h-8 px-2 rounded-full bg-primary/20 flex items-center justify-center">
                  <Key className="w-4 h-4 text-primary" />
                </div>
                <h3 className="text-4xl font-bold">For Studios</h3>
              </div>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Distribute your games and connect with content creators worldwide
              </p>
            </div>
            {renderSteps(studioSteps, "primary", "primary")}
          </TabsContent>

          <TabsContent value="gamers" className="mt-0">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-3 mb-4">
                <div className="w-8 h-8 px-2 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Gamepad2 className="w-4 h-4 text-secondary" />
                </div>
                <h3 className="text-4xl font-bold">For Gamers</h3>
              </div>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Discover indie games, earn rewards, and be part of our gaming community
              </p>
            </div>
            {renderSteps(playerSteps, "secondary", "secondary")}
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};
